import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui';
import type { CoverageBoardVM } from './useCoverageBoard';
import type { AppCoverageRow, CoverageTab } from './types';
import { appTileLabel } from './coverageHelpers';

/**
 * EAA-1817: left rail — "Ứng dụng tích hợp {n}", the filter input, the
 * "Tất cả / Cần xử lý / Chưa phủ" tabs, then one row per registry app. Rows
 * are real `<button>`s (not clickable `<div>`s) so the whole rail is Tab/Enter
 * reachable without any extra keyboard wiring.
 */
export function AppCoverageRail({ vm }: { vm: CoverageBoardVM }) {
    const { t } = useTranslation();

    // EAA-1845: built inside the component (not module scope) so the labels
    // can go through t() — these used to be plain module-level constants.
    const tabLabels: Record<CoverageTab, string> = {
        all: t('console.roleCoverage.rail.tabs.all'),
        needsAttention: t('console.roleCoverage.rail.tabs.needsAttention'),
        uncovered: t('console.roleCoverage.rail.tabs.uncovered'),
    };

    const dotStatusText: Record<AppCoverageRow['dotTone'], string> = {
        ok: t('console.roleCoverage.rail.dotStatus.ok'),
        warn: t('console.roleCoverage.rail.dotStatus.warn'),
        off: t('console.roleCoverage.rail.dotStatus.off'),
        muted: t('console.roleCoverage.rail.dotStatus.muted'),
    };

    return (
        <section className="rc-rail" aria-label={t('console.roleCoverage.rail.ariaLabel')}>
            <div className="rc-rail-head">
                <h2 className="rc-rail-title">
                    {t('console.roleCoverage.rail.title', { count: vm.appRows.length })}
                </h2>
            </div>

            {/* EAA-1817: raw <input> with a hand-drawn magnifier → kit Input, icon
                moved into its `prefix` slot (mirrors AppsToolbar's search box). */}
            <Input
                className="hz-search"
                placeholder={t('console.roleCoverage.rail.filterPlaceholder')}
                value={vm.search}
                onChange={(e) => vm.setSearch(e.target.value)}
                aria-label={t('console.roleCoverage.rail.filterAriaLabel')}
                prefix={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                }
            />

            <div
                className="rc-rail-tabs"
                role="group"
                aria-label={t('console.roleCoverage.rail.tabsAriaLabel')}
            >
                {(Object.keys(tabLabels) as CoverageTab[]).map((tab) => {
                    const count =
                        tab === 'needsAttention'
                            ? vm.needsAttentionCount
                            : tab === 'uncovered'
                              ? vm.uncoveredCount
                              : undefined;
                    const isActive = vm.tab === tab;
                    return (
                        <button
                            key={tab}
                            type="button"
                            className={'rc-rail-tab' + (isActive ? ' rc-rail-tab--active' : '')}
                            aria-pressed={isActive}
                            onClick={() => vm.setTab(tab)}
                        >
                            {tabLabels[tab]}
                            {typeof count === 'number' && count > 0 && (
                                <span className="rc-rail-tab-count">{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {vm.visibleRows.length === 0 ? (
                <p className="portal-admin-hint">{t('console.roleCoverage.rail.noMatch')}</p>
            ) : (
                <ul className="rc-rail-list">
                    {vm.visibleRows.map((row) => (
                        <li key={row.code}>
                            <button
                                type="button"
                                className={
                                    'rc-app-row' +
                                    (row.code === vm.selectedAppCode ? ' rc-app-row--selected' : '')
                                }
                                aria-current={row.code === vm.selectedAppCode ? 'true' : undefined}
                                aria-label={t('console.roleCoverage.rail.rowAriaLabel', {
                                    name: row.name,
                                    covered: row.coveredCount,
                                    total: row.totalRoleKeys,
                                    statusText: dotStatusText[row.dotTone],
                                })}
                                onClick={() => vm.selectApp(row.code)}
                            >
                                <span className="rc-app-row-top">
                                    <span className="rc-app-tile" aria-hidden="true">
                                        {appTileLabel(row.code)}
                                    </span>
                                    <span className="rc-app-row-name">{row.name}</span>
                                    <span
                                        className={`rc-app-row-dot rc-app-row-dot--${row.dotTone}`}
                                        aria-hidden="true"
                                    />
                                </span>
                                <span className="rc-app-row-progress">
                                    <span className="rc-app-row-progress-track">
                                        {/* EAA-1846 (design frames 52-61, screen 19 "coverage
                                            heatmap"): the fill was a flat --color-primary
                                            regardless of how covered the app was — the spec
                                            calls for a threshold tone (success >= 50%, warning
                                            below, and left at the track's own neutral color when
                                            nothing is covered yet) so the bar itself signals
                                            "needs attention" the same way the rail's health dot
                                            already does, purely a presentational derivation from
                                            counts already on the row. */}
                                        <span
                                            className={
                                                'rc-app-row-progress-fill' +
                                                (row.coveredCount === 0
                                                    ? ' rc-app-row-progress-fill--empty'
                                                    : row.coveredCount / row.totalRoleKeys >= 0.5
                                                      ? ' rc-app-row-progress-fill--ok'
                                                      : ' rc-app-row-progress-fill--warn')
                                            }
                                            style={{
                                                width:
                                                    row.totalRoleKeys > 0
                                                        ? `${Math.round((row.coveredCount / row.totalRoleKeys) * 100)}%`
                                                        : '0%',
                                            }}
                                        />
                                    </span>
                                    <span className="rc-app-row-progress-label">
                                        {row.coveredCount} / {row.totalRoleKeys}
                                    </span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

export default AppCoverageRail;
